import axios from 'axios';
import getConfig from 'next/config';

import {
  addUserGroupToData,
  buildFullLocation,
  format,
  getStateAbbr,
  getStateName,
  isStateAbbr,
  isStateName,
} from '../utils';

import { AuthService } from './authService';
import Service from './service';
const _isEmpty = require('lodash/isEmpty');
import { HttpRoutes } from 'utils/constant';
import { gtm } from 'utils/gtm';

const {
  publicRuntimeConfig: { USER_RATING_AUTOSUGGEST_VALUE, USER_BEHAVIOUR_CATEGORIES },
} = getConfig();

export const employerSignUp = (data) => {
  // Also add the referer here
  data.referer = document.referrer;
  data.signupPageUrl = window.location.href;
  addUserGroupToData(data);
  return axios
    .post(HttpRoutes.employerSignUp, data)
    .then((response) => {
      return response?.data || {};
    })
    .catch((error) => {
      if (error.response) {
        // The request was made and the server response was !== 2xx.
        const errorData = error?.response?.data;
        const errText = errorData?.result?.errText;

        if (errText === 'EMAIL_ALREADY_CLAIMED_COMPANY') {
          const errorMsg = 'Sorry, seems like you already claimed the profile of another company.';
          return Promise.reject({ data: { errorMsg, errText } });
        }

        if (errText === 'PASSWORD_MISMATCH') {
          const errorMsg = 'This email address is already used.';
          return Promise.reject({ data: { errorMsg, errText } });
        }

        if (errorData?.status === 'INVALID') {
          return Promise.reject({
            data: {
              errorMsg: errorData?.result?.userValidationError || errText,
              triggerSignupRecaptcha: errorData?.result?.triggerSignupRecaptcha,
              status: errorData?.status,
            },
          });
        }
      } else {
        // No response from the server, or some other error.
        return Promise.reject({
          data: {
            errorMsg: 'Something went wrong. Please try again later',
          }
        })
      }
    });
};

export const addHubSpotLeadNote = (data) => {
  return Service.post(HttpRoutes.addHubSpotLeadNote, data)
    .then((response) => Promise.resolve(response.data))
    .catch((error) => Promise.reject(error));
};

export const updateLeadDealSize = (data) => {
  return Service.post(HttpRoutes.updateLeadDealSize, data)
    .then((response) => Promise.resolve(response.data))
    .catch((error) => Promise.reject(error));
}

export const updateSalaryReport = (data) => {
  return Service.post(HttpRoutes.updateSalaryReport, data)
    .then((response) => Promise.resolve(response.data))
    .catch((error) => Promise.reject(error));
};

export const updateUserNotifications = (data) => {
  return Service.put(HttpRoutes.updateUserNotifications, data)
    .then((response) => Promise.resolve(response.data))
    .catch((error) => Promise.reject(error));
};

export const getUserNotifications = () => {
  return Service.get(HttpRoutes.getUserNotifications)
    .then((response) => Promise.resolve(response.data))
    .catch((error) => Promise.reject(error));
};

export const getActivePushSubscriptions = () => {
  return Service.get(HttpRoutes.getActivePushSubscriptions)
    .then((response) => Promise.resolve(response.data))
    .catch((error) => Promise.reject(error));
};

export const fetchUserFavouriteCompanies = (user) => {
  if (user && user.isLoggedIn) {
    // getUserFavouriteCompanies
    return Service.get(HttpRoutes.fetchUserFavouriteCompanies)
      .then((response) => response.data)
      .catch(() => []);
  }
  return Promise.resolve([]);
};

export const updateUserProfile = (obj) =>
  Service.post(HttpRoutes.updateUserProfile, obj).catch((err) => Promise.reject(err));

export const toggleUserFavouriteCompany = (companyInfo, favouriteCompanies, forcedSave) => {
  if (!companyInfo || typeof companyInfo !== 'object' || !Array.isArray(favouriteCompanies)) {
    console.error(`Error occurred while toggling user favourite company. Error: invalid params`);
    return Promise.resolve([]);
  }

  const isSaved = favouriteCompanies.some((item) => item.companyID === companyInfo.companyID);
  let surveySelectedCompanies;
  if (isSaved) {
    surveySelectedCompanies = forcedSave
      ? favouriteCompanies
      : favouriteCompanies.filter((item) => item.companyID !== companyInfo.companyID);
  } else {
    const addCompany = {
      companyName: companyInfo.companyNameCPS,
      companyID: companyInfo.companyID,
      key: companyInfo.companyName,
      selected: true,
    };
    surveySelectedCompanies = [...favouriteCompanies, addCompany];
  }

  const payload = {
    surveySelectedCompanies,
  };

  return updateUserProfile(payload)
    .then(() => surveySelectedCompanies)
    .catch((error) => {
      console.error(
        `Error occurred while toggling user favourite company. Error: ${JSON.stringify(error)}`
      );
      return Promise.resolve([]);
    });
};

export const getEasyApplyUserInfo = async () => {
  try {
    const userInfo = await Service.get(HttpRoutes.getEasyApplyUserInfo);
    if (userInfo && userInfo.data) {
      return userInfo.data;
    }
    throw new Error('Error while get EasyApplyUserInfo');
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * user/easy-apply
 * @param {Object} payload
 * @param {String} payload.email
 * @param {String} payload.firstName
 * @param {String} payload.phone
 * @param {String} payload.jobId
 * @param {String} payload.lastName
 * @param {String} payload.listingHash
 * @param {Array<String>} payload.resumeFiles
 */
export const userEasyApply = async (payload) => {
  try {
    addUserGroupToData(payload);
    payload.disableEmailSandbox = window?.disableEmailSandbox || false;
    payload.signupPageUrl = window.location.href;
    const response = await Service.post(HttpRoutes.easyApply, payload);
    if (response && response.data) {
      const { data } = response;
      if (data.status === 'INVALID') {
        return Promise.reject(data.result);
      }
      return data;
    }
    throw new Error('Error in userEasyApply');
  } catch (e) {
    return Promise.reject(e);
  }
};

export const addApplied = async (payload) => {
  try {
    const response = await Service.post(HttpRoutes.addApplied, payload);
    if (response && response.data === 'SUCCESS') {
      return response.data;
    }
    throw new Error('Error in userService.addApplied');
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * @param {Object} payload
 * @param {Number | String} payload.begin
 */
export const getUserDismissedJobs = async (payload) => {
  const { begin = 0 } = payload;
  const url = format(HttpRoutes.userDismissedJob, { begin });
  try {
    const response = await Service.get(url);
    if (response && response.data) {
      return response.data;
    }
    throw new Error('Error in userService.getUserDismissedJobs');
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * @param {Object} payload
 * @param {Number | String} payload.begin
 */
export const getUserSeenJobs = async (payload) => {
  const { begin = 0 } = payload;
  const url = format(HttpRoutes.userSeenJob, { begin });
  try {
    const response = await Service.get(url);
    if (response && response.data) {
      return response.data;
    }
    throw new Error('Error in userService.getUserSeenJobs');
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * @param {Object} payload
 * @param {Number | String} payload.begin
 */
export const getUserProfile = async () => {
  try {
    const response = await Service.get(HttpRoutes.getUserProfile);
    if (response && response.data) {
      return response.data;
    }
    throw new Error('Error in userService.getUserProfile');
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getInitialJobBuckets = (payload) => {
  const { jobByListingHash } = payload || {};
  if (jobByListingHash && payload.numJobs) {
    payload.numJobs = payload.numJobs - 1;
  }
  delete payload.jobByListingHash;
  return Service.post(HttpRoutes.userMatchesLoad, payload)
    .then((response) => {
      const dataResponse = response.data;
      if (jobByListingHash && dataResponse?.listBuckets?.[0]?.normal?.jobs) {
        const jobs = dataResponse.listBuckets[0].normal.jobs || [];
        dataResponse.listBuckets[0].normal.jobs = [
          jobByListingHash,
          ...jobs,
        ];
      }
      return Promise.resolve(dataResponse);
    })
    .catch((err) => Promise.reject(err));
}

export const touchBucket = (obj) =>
  Service.post(HttpRoutes.touchBucket, obj)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));

export const sendReCaptchaResponse = (obj) =>
  Service.post(HttpRoutes.verifyReCaptcha, obj)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));

export const sendSignupReCaptchaResponse = (obj) =>
  Service.post(HttpRoutes.verifySignupReCaptcha, obj)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));

export const removeUserResume = (payload) =>
  Service.post(HttpRoutes.removeUserResume, payload)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));

export const updateUserRating = (data = {}) => {
  let { title, city, state, major, companyID, ratingChange, location, extendedLocation } =
    data || {};
  const payload = {};

  const tempStateAbbr = getStateAbbr(state);
  const tempState = getStateName(tempStateAbbr);

  const jobLocation = buildFullLocation(city, tempState, tempStateAbbr);

  if (title) {
    payload.title = title;
  }
  if (location || jobLocation) {
    payload.location = location || jobLocation;
  }
  if (extendedLocation) {
    payload.extendedLocation = extendedLocation;
  }
  if (major) {
    payload.major = major;
  }
  if (companyID) {
    payload.companyID = companyID;
  }

  if (_isEmpty(payload)) {
    return Promise.resolve({ updated: false });
  }

  if (ratingChange) {
    payload.ratingChange = ratingChange;
  }

  if (window && window.userId) {
    payload.userId = window.userId;
  }

  if (payload.location && payload.location.toLowerCase() === 'district of columbia') {
    payload.location = 'District Of Columbia';
  }

  return Service.post(HttpRoutes.updateUserRating, payload)
    .then((response) => {
      if (response && response.data && !response.data.success && !data.retry) {
        // Retry updating user rating in case if userId hasn't been created yet for brand new session
        setTimeout(() => {
          return updateUserRating({ ...data, retry: true });
        }, 10000);
      } else {
        return response;
      }
    })
    .catch((error) => {
      console.error('Error in userService.updateUserRating', error);
      return error;
    });
};

export const updateUserBehaviour = (data = {}) => {
  let { title, city, stateAbbr, state, major, companyID, category } = data || {};
  const payload = {};

  stateAbbr = isStateAbbr(stateAbbr) ? stateAbbr : '';
  state = isStateName(state) ? state : '';

  const userLocation = city && stateAbbr ? `${city}, ${stateAbbr}` : state;

  if (title) {
    payload.title = title;
  }
  if (userLocation) {
    payload.userLocation = userLocation;
  }
  if (major) {
    payload.major = major;
  }
  if (companyID) {
    payload.companyID = companyID;
  }
  if (category) {
    payload.category = category;
  }

  if (_isEmpty(payload)) {
    return Promise.resolve({ updated: false });
  }

  if (window && window.userId) {
    payload.userId = window.userId;
  }

  if (payload.userLocation && payload.userLocation.toLowerCase() === 'district of columbia') {
    payload.userLocation = 'District Of Columbia';
  }

  return Service.post(HttpRoutes.updateUserBehaviour, payload)
    .then((response) => {
      if (response && response.data && !response.data.success && !data.retry) {
        // Retry updating user behaviour in case if userId hasn't been created yet for brand new session
        setTimeout(() => {
          return updateUserBehaviour({ ...data, retry: true });
        }, 10000);
      } else {
        return response;
      }
    })
    .catch((error) => {
      console.error('Error in userService.updateUserBehaviour', error);
      return error;
    });
};

export const trackUserAutosuggestActivities = (data = {}) => {
  const { category, valueObj = {} } = data || {};
  const { original_name, city, state, companyID } = valueObj;

  let payload = {};

  if (category === 'jobTitle') {
    payload = {
      ...payload,
      ratingChange: USER_RATING_AUTOSUGGEST_VALUE.TITLE,
      title: original_name || '',
    };
  }
  if (category === 'location') {
    payload = {
      ...payload,
      ratingChange: USER_RATING_AUTOSUGGEST_VALUE.LOCATION,
      state: getStateName(state) || '',
      stateAbbr: getStateAbbr(state) || '',
      city,
    };
  }
  if (category === 'major') {
    payload = {
      ...payload,
      ratingChange: USER_RATING_AUTOSUGGEST_VALUE.MAJOR,
      major: original_name || '',
    };
  }
  if (category === 'company') {
    payload = {
      ...payload,
      ratingChange: USER_RATING_AUTOSUGGEST_VALUE.COMPANY,
      companyID,
    };
  }

  if (!_isEmpty(payload)) {
    updateUserRating(payload)
      .then(() => {})
      .finally(() => {
        updateUserBehaviour(payload);
      });
  }
};

export const trackUserPageViewActivities = (criteria = {}) => {
  const {
    title = '',
    city = '',
    state,
    extendedLocation = '',
    major = '',
    companyID = '',
    isSalaryJobPage = false,
    isCareerSkillsPage = false,
    isCareerRemoteJobsPage = false,
  } = criteria || {};

  const stateAbbrTemp = getStateAbbr(state);
  const stateTemp = getStateName(stateAbbrTemp);

  const hasData = !!(title || city || stateAbbrTemp || stateTemp || major || companyID);

  if (hasData) {
    let userBehaviourCategory = USER_BEHAVIOUR_CATEGORIES.PAGE_HIT;
    if (isSalaryJobPage) {
      userBehaviourCategory = USER_BEHAVIOUR_CATEGORIES.SALARIES_HIT;
    } else if (isCareerSkillsPage) {
      userBehaviourCategory = USER_BEHAVIOUR_CATEGORIES.SKILL_HIT;
    }

    const payload = {
      title,
      city,
      stateAbbr: stateAbbrTemp,
      state: stateTemp,
      extendedLocation,
      major,
      companyID,
      category: userBehaviourCategory,
    };

    if (isCareerRemoteJobsPage) {
      payload.extendedLocation = 'Work From Home';
    }

    if (!_isEmpty(payload)) {
      updateUserRating(payload)
        .then(() => {})
        .finally(() => {
          updateUserBehaviour(payload);
        });
    }
  }
};

export function deleteZohoCandidate(userId = '', zohoCandidateId = '') {
  return Service.post(HttpRoutes.deleteZohoCandidate, { userId, zohoCandidateId })
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

export function deleteResumeInfo(userId = '') {
  return Service.put(HttpRoutes.resumeBuilderData, { userId, deleted: true })
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

export function deactiveStripeAccount() {
  return Service.delete(HttpRoutes.deactiveStripeAccount)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

/**
 * 
 * @param {{
 *  planCategory: typeof PLAN_CATEGORIES[keyof typeof PLAN_CATEGORIES];
 *  plan: import('types').StripeSubscriptionPlan;
 *  [key: string]: any;
 * }} data 
 * @returns 
 */
export function purchaseStripeSubscription(data) {
  gtm.logBeginCheckout(data.planCategory, data.plan);

  const body = {
    ...data,
    planName: data.plan.KEY,
  };

  delete body.plan;

  return Service.post(HttpRoutes.purchaseStripeSubscription, body)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

/**
 * Add a new job posting
 * @param {import('types/index').JobPosting} jobPosting
 * @returns {Promise<{ jobId: string; redirectTo: string; }>}
 */
export function addEmployerJobPosting(jobPosting) {
  return Service.post(HttpRoutes.addEmployerJobPosting, jobPosting)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

export function updateEmployerJobPosting(data) {
  return Service.post(HttpRoutes.updateEmployerJobPosting, data)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

export function fetchEmployerJobPostings() {
  return Service.get(HttpRoutes.fetchEmployerJobPostings)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

/**
 * Post a job which is in "Draft" state
 * @param {{
 * jobPosting: import('types/index').JobPosting;
 * subscriptionId: string;
 * budgetPerJob: string;
 * }} data
 * @returns {Promise<{success: boolean;}>}
 */
export function postADraftJob(data) {
  return Service.post(HttpRoutes.postADraftJob, data)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

/**
 * PUT review feedback
 * @param {{
 * reviewId: string;
 * userId: string;
 * reviewHelpful: boolean;
 * }} data
 * @returns {Promise<{success: boolean;}>}
 * */
export function putReviewFeedback(data) {
  return Service.put(HttpRoutes.putReviewFeedback, data)
    .then((response) => response)
    .catch((err) => Promise.reject(err));
}

/**
 * Reopens a job which is in "Closed" state, maintaining the same jobId and other details.
 * @param {{
 *  jobPosting: import('types/index').JobPosting;
 *  numJobPostings: number;
 *  totalActiveOrInReviewJobPostings: number;
 * }} data
 * @returns {Promise<{success: boolean;}>}
 */
export function reopenAClosedJob(data) {
  return Service.post(HttpRoutes.reopenAClosedJob, data)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

/**
 * Close a job posting
 * @param {{ jobPosting: import('types/index').JobPosting; }} data
 * @returns {Promise<{success: boolean;}>}
 */
export function closeAPostedJob(data) {
  return Service.post(HttpRoutes.closeAPostedJob, data)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

/**
 * Delete a job posting
 * @param {{ jobPosting: import('types/index').JobPosting; }} data
 * @returns {Promise<{success: boolean;}>}
 */
export function deleteAPostedJob(data) {
  return Service.post(HttpRoutes.deleteAPostedJob, data)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

/**
 * Fetch applications for a job posting.
 * @param {{
 *  jobIds?: Array<string>; // Optional. If not provided, all applications will be returned.
 *  userId?: string; // Optional.
 *  interested?: boolean;
 *  start?: number; // Starting index of the paginated result.
 *  noOfItems?: number; // No of items per page.
 *  customFilter?: 'custom_value_1' | 'custom_value_2'; // Optional.
 * }} data
 * @returns {Promise<{
 *  jobPostingApplications: Array<any> // List of applications.
 *  total: number; // Total number of applications.
 * }>}
 */
export function fetchJobPostingApplications(data) {
  return Service.post(HttpRoutes.fetchJobPostingApplications, data)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

/**
 * Updates the interested status of the application.
 * @param {{
 *  applicationId: string; // Required. ApplicationId of the application.
 *  interested: boolean; // Required. True if interested, false otherwise.
 * }} data
 * @returns
 */
export function updateApplicationInterest(data) {
  return Service.post(HttpRoutes.updateApplicationInterest, data)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

/**
 * Updates the manualVerification field of the application.
 * @param {{
 *  applicationId: string; // Required. ApplicationId of the application.
 *  manualVerification: 'Valid' | 'Invalid'. // Required.
 * }} data
 * @returns
 */
export function verifyApplication(data) {
  return Service.post(HttpRoutes.verifyApplication, data)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

/**
 * Post career quiz results
 * @param {object} careerQuizResults
 */
export function putCareerQuizResult(careerQuizResults) {
  return Service.put(HttpRoutes.putCareerQuizResults, careerQuizResults)
    .then((response) => response.data)
    .catch((err) => Promise.reject(err));
}

/**
 * This method is responsible for doing certain actions as mention below
 ** Deletes user's all saved resumes from ResumeInfo Collection
 ** Deletes user's Zoho candidate profile
 ** Cancels the subscription and deletes customer profile on stripe
 ** Deletes user from UserInfo collection and adds flags the document as `deleted`
 * @param {{ userId: string; zohoCandidateId?: string; }} user User which needs to be deleted
 * @param {{ userId: string; [other: string]: unknown; }} userDeleteData Some additonal user data (if any)
 * @param {string} pageCategory pageCategory of the page, since the user may be deleted either from admin page (by Zippia Admin) or profile page (by User itself)
 */
export async function deleteUser(user, userDeleteData = {}, pageCategory = window.pageCategory) {
  try {
    // 1. Delete User's resumeInfo (if any)
    deleteResumeInfo(user.userId).catch((err) => {
      console.error("Error occurred while deleting user's resumeInfo", err);
    });

    // 2. Delete User's Zoho Profile (if any)
    if (user.zohoCandidateId) {
      deleteZohoCandidate(user.userId, user.zohoCandidateId).catch((err) => {
        console.error("Error occurred while deleting user's Zoho candidate profile", err);
      });
    }

    // 3. Cancel user's subscription and deletes customer profile ons stripe (if any)
    deactiveStripeAccount().catch((err) => {
      console.error("Error occurred while cancelling user's subscription on stripe", err);
    });

    // 4. Delete Zippia user
    if (userDeleteData.userId) {
      let url = `/${HttpRoutes.deactivateAccount}?userId=${userDeleteData.userId}`;
      await Service.get(url);
    } else {
      await AuthService.deactivateAccount(userDeleteData);
    }
    AuthService.logDeleteUserActivity({ user, pageCategory });
  } catch (err) {
    throw 'The following error occurred while deleting user:' + err.message;
  }
}
